import { type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Input } from "../../../components/ui/Input";
import { cn } from "../../../lib/utils";

export function NameStep({
  firstName,
  lastName,
  onFirstNameChange,
  onLastNameChange,
  onNext,
  onSignupStarted,
  hasSignupStarted,
  isActive,
  countryField,
  variant,
}: {
  firstName: string;
  lastName: string;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onNext: () => void;
  onSignupStarted: () => void;
  hasSignupStarted: boolean;
  isActive: boolean;
  /**
   * The country question, when it is asked HERE rather than on a slide of its
   * own — which is the desktop grouping (Figma 7092-5913). `null` on mobile,
   * where the country keeps its own screen.
   *
   * A slot rather than a second form: this form owns the react-hook-form
   * instance, the `onboarding-form-name` id the footer Continue submits, and
   * the uncontrolled-input workaround below. Two forms on one screen would
   * need a second Continue.
   */
  countryField: ReactNode | null;
  /**
   * `desktop` puts the two names on one row and spaces the questions as the
   * desktop screen draws them (Figma "D7.identite.default").
   */
  variant: "mobile" | "desktop";
}) {
  const { t } = useTranslation();

  // Uncontrolled inputs (RHF `register`) — the value is NOT reassigned onto the
  // DOM node on every keystroke. A controlled value that round-trips through the
  // session store resets the caret to position 0 mid-IME-composition on the
  // Samsung keyboard, which types words backwards ("robin" → "nibor"). The store
  // is still kept live via onChange so the parent's step validity / signup read
  // the latest values.
  //
  // `values` reseeds the fields when the store changes externally — e.g. an SSO
  // user's name is prefilled asynchronously after currentUser loads — while
  // `keepDirtyValues` preserves anything the user has already typed, so the
  // reseed never clobbers an in-progress edit (and never re-sets `.value`
  // mid-typing, which would resurrect the caret bug).
  const { register, handleSubmit } = useForm({
    values: { firstName, lastName },
    resetOptions: { keepDirtyValues: true },
  });

  const firstNameField = register("firstName", {
    validate: (v) => v.trim().length > 0,
  });
  const lastNameField = register("lastName", {
    validate: (v) => v.trim().length > 0,
  });

  return (
    <form
      id="onboarding-form-name"
      className="flex flex-1 flex-col w-full"
      onSubmit={handleSubmit(() => {
        onNext();
      })}
    >
      <div
        className={cn(
          "flex flex-col items-start",
          variant === "mobile" ? "gap-3" : "gap-6"
        )}
      >
        <div
          className={cn(
            "w-full gap-3",
            variant === "mobile" ? "flex flex-col" : "grid grid-cols-2"
          )}
        >
          <div className="flex flex-col gap-1 w-full">
            <label className="font-['DM_Sans',sans-serif] text-[13px]/5  tracking-[-0.13px] text-black">
              {t("First Name")}
            </label>
            <Input
              variant="ghost"
              className={cn(
                "bg-white rounded-xl",
                variant === "mobile"
                  ? "border-[#c1c1c1]"
                  : "border-ds-slate-200"
              )}
              placeholder={t("John")}
              autoFocus={isActive}
              autoComplete="given-name"
              {...firstNameField}
              onChange={(e) => {
                void firstNameField.onChange(e);
                onFirstNameChange(e.currentTarget.value);
                if (!hasSignupStarted) {
                  onSignupStarted();
                }
              }}
            />
          </div>

          <div className="flex flex-col gap-1 w-full">
            <label className="font-['DM_Sans',sans-serif] text-[13px]/5  tracking-[-0.13px] text-black">
              {t("Last Name")}
            </label>
            <Input
              variant="ghost"
              className={cn(
                "bg-white rounded-xl",
                variant === "mobile"
                  ? "border-[#c1c1c1]"
                  : "border-ds-slate-200"
              )}
              placeholder={t("Doe")}
              autoComplete="family-name"
              {...lastNameField}
              onChange={(e) => {
                void lastNameField.onChange(e);
                onLastNameChange(e.currentTarget.value);
              }}
            />
          </div>
        </div>

        {countryField}
      </div>
    </form>
  );
}
